/**
 * Converts a string from snake_case to camelCase
 */
export function toCamelCase(str: string): string {
  return str.replace(/_([a-z0-9])/g, (_, char) => char.toUpperCase());
}

/**
 * Decodes HTML entities in a string.
 *
 * Django stored text through its escape() function, encoding &, <, >, ", '
 * as HTML entities. React renders text literally (not as HTML), so these
 * entities appear as raw "&amp;" instead of "&". This decoder reverses
 * Django's encoding at the API response boundary.
 *
 * Handles named entities (amp, lt, gt, quot, apos) and numeric entities
 * (decimal &#NNN; and hex &#xHH;).
 */
const HTML_ENTITY_PATTERN = /&(?:#(\d+)|#x([0-9a-fA-F]+)|(\w+));/g;

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: '\u00A0',
};

export function decodeHtmlEntities(str: string): string {
  if (!str.includes('&')) return str;
  return str.replace(HTML_ENTITY_PATTERN, (match, decimal, hex, named) => {
    if (decimal) return String.fromCharCode(parseInt(decimal, 10));
    if (hex) return String.fromCharCode(parseInt(hex, 16));
    if (named && named in NAMED_ENTITIES) return NAMED_ENTITIES[named]!;
    return match; // Unknown named entity — leave as-is
  });
}

/**
 * ISO 8601 date string pattern (without timezone)
 * Matches: 2026-02-12T10:30:00 or 2026-02-12T10:30:00.123456
 */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/;

/**
 * Normalizes an ISO date string to include UTC timezone indicator.
 * Server sends timestamps without timezone suffix - we treat them as UTC.
 * Returns the original string if it's not an ISO date or already has timezone.
 */
export function normalizeUTCDateString(value: string): string {
  if (typeof value !== 'string') return value;

  // Already has timezone indicator (Z, +XX:XX, or -XX:XX after position 10)
  if (value.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(value)) {
    return value;
  }

  // Matches ISO date without timezone - append Z to treat as UTC
  if (ISO_DATE_PATTERN.test(value)) {
    return value + 'Z';
  }

  return value;
}

/**
 * Converts a string from camelCase / PascalCase to snake_case.
 *
 * Index-aware boundary insertion:
 *  - Insert `_` between a lowercase/digit and an uppercase
 *    (`firstName` → `first_name`).
 *  - Insert `_` between consecutive uppercase letters when the next
 *    uppercase is followed by a lowercase, recognizing the start of a
 *    new word inside an acronym (`XMLParser` → `xml_parser`).
 *  - Never emit a leading underscore: `ID` → `id`, `FirstName` → `first_name`.
 */
export function toSnakeCase(str: string): string {
  return str
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .toLowerCase();
}

/**
 * Keys whose *keys* are opaque — user-defined names that must survive
 * case conversion verbatim, but whose string *values* are ordinary
 * user-visible text and therefore still get the response-boundary
 * treatment (HTML-entity decode + UTC date normalization).
 *
 * - `attributes`: contact attribute map — keys are user-defined dimension
 *   names ("Department", "Job Title"), values are user-entered text that
 *   Django stored HTML-escaped ("Hearing Scientist (R&amp;D)").
 * - `set_attributes`: bulk set-attributes map. Same shape. Without the
 *   key-opacity the request transform lowercased the dimension names
 *   ("department"), the backend's case-sensitive dimension lookup missed,
 *   and bulk Set Attributes silently no-opped (or, for admins, created
 *   duplicate lowercase dimensions). Found by the F-2 MSW wire-shape test.
 * - `initial_filters`: user-defined filter config — keys are filter names
 *   defined per-org in app config, values are display/filter strings.
 * - `contact_attributes`: the analytics attribute filter, `{"Job Title":
 *   ["Nurse"]}`. It rides GET requests as a JSON string (untouched either
 *   way), but the composite export (vingreact#87) posts it as a real object —
 *   without opacity the dimension names snake_cased ("job_title"), vingapi's
 *   case-sensitive dimension lookup missed, and the export silently matched
 *   nobody.
 *
 * Opacity here was originally implemented as "don't descend at all", which
 * also skipped `decodeHtmlEntities` for the values. That is why a job title
 * stored as `Hearing Scientist (R&amp;D)` rendered literally on ContactDetail
 * while the contact's name (a non-opaque key) rendered decoded.
 *
 * **Array rule (#437):** when a key-opaque key's direct value is an array,
 * the array is structural (e.g. `RecipientSearchResponse.attributes` is
 * `[{name, value, contact_count}]`, not a user-keyed map). In that case the
 * items are converted normally (recursing with the ordinary rules, so a
 * nested opaque key inside an item is still honoured). Object values keep
 * the existing behaviour (keys verbatim, string leaves decoded). A
 * user-keyed map is always an object, never an array.
 */
export const KEY_OPAQUE_KEYS = new Set([
  'attributes',
  'set_attributes',
  'initial_filters',
  'contact_attributes',
]);

/**
 * Keys whose entire value is opaque — raw third-party / user-supplied
 * payloads that are round-tripped back to the server byte-for-byte and must
 * never be rewritten in any way (not keys, not string leaves).
 *
 * - `sample`/`samples`: raw third-party API data (e.g. ADP worker JSON).
 *   `getAdpSample` fetches it and `previewMappings`/`discoverFields` POST the
 *   very same objects back (see features/integrations/api/mappings.ts), so
 *   decoding an `&amp;` inside a worker record would corrupt the round trip
 *   and change what the mapping preview is computed against.
 *
 * NOTE: do not add field names that some endpoints use structurally. `workers`
 * is *not* global: the ADP sample endpoint holds raw third-party worker JSON
 * (opaque), but the work-queue `/workers-lanes/` endpoint returns structured
 * `Worker` rows that must be camelCased like any other response. Because both
 * are top-level `workers` keys, the only honest disambiguator is the request —
 * so opacity for `workers` is opted in per request (see `getAdpSample`) via the
 * `extraOpaqueKeys` parameter below, not registered globally here.
 * `extraOpaqueKeys` always means *fully* verbatim.
 */
export const VERBATIM_KEYS = new Set([
  'sample',
  'samples',
]);

/**
 * Every key the transforms treat as opaque in some way. The request
 * (camel→snake) transform makes no string rewrites at all, so it only needs
 * key opacity and can use the union directly.
 */
const OPAQUE_KEYS = new Set([...KEY_OPAQUE_KEYS, ...VERBATIM_KEYS]);

// Binary / non-plain values that must never be descended into. They carry their
// own internal key structure (multipart field names, URL params, binary blobs)
// that case conversion would corrupt. Bug-hunt #1: `convertKeysToSnake` used to
// match FormData via `typeof === 'object'` and silently drop every entry.
function isOpaqueBoundaryValue(data: unknown): boolean {
  if (data === null || typeof data !== 'object') return false;
  if (data instanceof Date) return true;
  if (typeof FormData !== 'undefined' && data instanceof FormData) return true;
  if (typeof Blob !== 'undefined' && data instanceof Blob) return true;
  if (typeof File !== 'undefined' && data instanceof File) return true;
  if (typeof URLSearchParams !== 'undefined' && data instanceof URLSearchParams) return true;
  if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) return true;
  if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(data)) return true;
  return false;
}

/**
 * Applies the response-boundary string treatment (HTML-entity decode + UTC
 * date normalization) to every string leaf of a subtree while leaving every
 * object key exactly as the server sent it.
 *
 * This is what "opaque" means for `KEY_OPAQUE_KEYS`: the *names* are
 * user-defined and must not be camel-cased, but the *values* are ordinary
 * user-visible text and get decoded like any other response string.
 *
 * A `VERBATIM_KEYS` / `extraOpaqueKeys` key nested inside such a subtree is
 * still returned untouched.
 */
function decodeOpaqueKeyedSubtree(
  data: unknown,
  extraOpaqueKeys?: ReadonlySet<string>
): unknown {
  if (Array.isArray(data)) {
    return data.map((item) => decodeOpaqueKeyedSubtree(item, extraOpaqueKeys));
  }

  if (isOpaqueBoundaryValue(data)) {
    return data;
  }

  if (data !== null && typeof data === 'object') {
    const walked: Record<string, unknown> = {};
    for (const key of Object.keys(data)) {
      const value = (data as Record<string, unknown>)[key];
      walked[key] =
        VERBATIM_KEYS.has(key) || extraOpaqueKeys?.has(key)
          ? value
          : decodeOpaqueKeyedSubtree(value, extraOpaqueKeys);
    }
    return walked;
  }

  if (typeof data === 'string') {
    return normalizeUTCDateString(decodeHtmlEntities(data));
  }

  return data;
}

/**
 * Recursively converts all object keys from snake_case to camelCase.
 * Also decodes HTML entities and normalizes ISO date strings to UTC.
 *
 * Values under `KEY_OPAQUE_KEYS` keep their keys verbatim but still get their
 * string leaves decoded; values under `VERBATIM_KEYS` are passed through
 * completely untouched.
 *
 * `extraOpaqueKeys` adds request-scoped *fully verbatim* keys on top of the
 * global set, for fields that are structural on most endpoints but raw on one
 * (e.g. `workers` on the ADP sample endpoint). They apply to the whole subtree.
 */
export function convertKeysToCamel<T>(
  data: unknown,
  extraOpaqueKeys?: ReadonlySet<string>
): T {
  if (Array.isArray(data)) {
    return data.map((item) => convertKeysToCamel(item, extraOpaqueKeys)) as T;
  }

  if (isOpaqueBoundaryValue(data)) {
    return data as T;
  }

  if (data !== null && typeof data === 'object') {
    const converted: Record<string, unknown> = {};
    for (const key of Object.keys(data)) {
      const camelKey = toCamelCase(key);
      const isVerbatim =
        VERBATIM_KEYS.has(key) || VERBATIM_KEYS.has(camelKey) ||
        extraOpaqueKeys?.has(key) || extraOpaqueKeys?.has(camelKey);
      const isKeyOpaque =
        KEY_OPAQUE_KEYS.has(key) || KEY_OPAQUE_KEYS.has(camelKey);
      if (isVerbatim) {
        converted[camelKey] = (data as Record<string, unknown>)[key];
      } else if (isKeyOpaque) {
        const raw = (data as Record<string, unknown>)[key];
        // #437: arrays under a key-opaque key are structural (typed records),
        // not user-keyed maps — convert their items normally.
        if (Array.isArray(raw)) {
          converted[camelKey] = raw.map((item) =>
            convertKeysToCamel(item, extraOpaqueKeys),
          );
        } else {
          converted[camelKey] = decodeOpaqueKeyedSubtree(raw, extraOpaqueKeys);
        }
      } else {
        converted[camelKey] = convertKeysToCamel(
          (data as Record<string, unknown>)[key],
          extraOpaqueKeys
        );
      }
    }
    return converted as T;
  }

  // Decode HTML entities (Django legacy) then normalize ISO date strings to UTC
  if (typeof data === 'string') {
    return normalizeUTCDateString(decodeHtmlEntities(data)) as T;
  }

  return data as T;
}

/**
 * Recursively converts all object keys from camelCase to snake_case.
 * Values under OPAQUE_KEYS are passed through without key conversion.
 */
export function convertKeysToSnake<T>(data: unknown): T {
  if (Array.isArray(data)) {
    return data.map((item) => convertKeysToSnake(item)) as T;
  }

  if (isOpaqueBoundaryValue(data)) {
    return data as T;
  }

  if (data !== null && typeof data === 'object') {
    const converted: Record<string, unknown> = {};
    for (const key of Object.keys(data)) {
      const snakeKey = toSnakeCase(key);
      if (OPAQUE_KEYS.has(key) || OPAQUE_KEYS.has(snakeKey)) {
        const raw = (data as Record<string, unknown>)[key];
        // #437: arrays under an opaque key are structural — convert items normally.
        if (KEY_OPAQUE_KEYS.has(key) || KEY_OPAQUE_KEYS.has(snakeKey)) {
          if (Array.isArray(raw)) {
            converted[snakeKey] = raw.map((item) => convertKeysToSnake(item));
          } else {
            converted[snakeKey] = raw;
          }
        } else {
          converted[snakeKey] = raw;
        }
      } else {
        converted[snakeKey] = convertKeysToSnake(
          (data as Record<string, unknown>)[key]
        );
      }
    }
    return converted as T;
  }

  return data as T;
}
